if (!customElements.get('free-shipping-bar')) {
  const FSB_STORAGE_KEY = 'alpha_fsb_country';
  const FSB_COUNTRY_EVENT = 'alpha:fsb-country-change';

  const readStoredCountry = () => {
    try {
      return window.localStorage.getItem(FSB_STORAGE_KEY);
    } catch (e) {
      return null;
    }
  };

  const storeCountry = (code) => {
    try {
      window.localStorage.setItem(FSB_STORAGE_KEY, code);
    } catch (e) {}
  };

  class FreeShippingBar extends HTMLElement {
    constructor() {
      super();
      this.total = parseInt(this.dataset.total, 10) || 0;
      this.moneyFormat = (this.dataset.moneyFormat || '{{amount}}').replace(/<[^>]+>/g, '');
      this.textNext = this.dataset.textNext || '';
      this.textAll = this.dataset.textAll || '';
      this.textSuccess = this.dataset.textSuccess || '';
      this.messageEl = this.querySelector('[data-fsb-message]');
      this.fillEl = this.querySelector('[data-fsb-fill]');
      this.trackEl = this.querySelector('[role="progressbar"]');
      this.markersEl = this.querySelector('[data-fsb-markers]');
      this.labelsEl = this.querySelector('[data-fsb-labels]');
      this.discountEl = this.querySelector('[data-fsb-discount]');
      this.countryEl = this.querySelector('[data-fsb-country]');
      this.requestToken = 0;
      this.countries = this.readConfig();
      this.onCountryChange = () => this.render(this.total);
    }

    readConfig() {
      const node = this.querySelector('[data-fsb-config]');
      try {
        const config = JSON.parse(node ? node.textContent : '{}');
        return (config.countries || [])
          .map((country) => ({
            ...country,
            tiers: (country.tiers || []).filter((t) => t.amount > 0).sort((a, b) => a.amount - b.amount),
          }))
          .filter((country) => country.tiers.length);
      } catch (e) {
        return [];
      }
    }

    connectedCallback() {
      const events = typeof PUB_SUB_EVENTS !== 'undefined' ? PUB_SUB_EVENTS : null;
      const sub =
        typeof subscribe === 'function'
          ? subscribe
          : typeof window.subscribe === 'function'
          ? window.subscribe
          : null;

      if (events && events.cartUpdate && sub) {
        this.unsubscribe = sub(events.cartUpdate, (event) => this.onCartUpdate(event));
      }
      document.addEventListener(FSB_COUNTRY_EVENT, this.onCountryChange);

      this.render(this.total);
    }

    disconnectedCallback() {
      if (typeof this.unsubscribe === 'function') this.unsubscribe();
      document.removeEventListener(FSB_COUNTRY_EVENT, this.onCountryChange);
    }

    onCartUpdate(event) {
      const total =
        event && event.cartData && typeof event.cartData.original_total_price === 'number'
          ? event.cartData.original_total_price
          : null;

      if (total !== null) {
        this.render(total);
        return;
      }

      const token = ++this.requestToken;
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })
        .then((r) => r.json())
        .then((cart) => {
          if (token === this.requestToken) this.render(cart.original_total_price);
        })
        .catch(() => {});
    }

    getCountry() {
      const byCode = (code) => this.countries.find((c) => c.code === code);
      return byCode(readStoredCountry()) || byCode(this.dataset.defaultCountry) || this.countries[0];
    }

    render(total) {
      this.total = total || 0;
      const country = this.getCountry();
      if (!country) {
        this.hidden = true;
        return;
      }
      this.hidden = false;

      const tiers = country.tiers;
      let reached = -1;
      tiers.forEach((tier, i) => {
        if (this.total >= tier.amount) reached = i;
      });
      const qualified = reached === tiers.length - 1;
      const percent = this.progressFor(tiers, reached);
      const discount = tiers
        .slice(0, reached + 1)
        .reduce((max, tier) => (tier.type === 'discount' && tier.percent > max ? tier.percent : max), 0);

      if (this.fillEl) this.fillEl.style.width = `${percent}%`;
      if (this.trackEl) this.trackEl.setAttribute('aria-valuenow', String(Math.round(percent)));
      this.style.setProperty('--fsb-progress', String(percent));
      this.classList.toggle('free-shipping-bar--qualified', qualified);

      this.renderMessage(tiers, reached, qualified, discount);
      this.renderSteps(tiers, reached);
      this.renderDiscount(country, discount);
      this.renderCountry(country);
    }

    // Stufen liegen gleichmaessig verteilt auf dem Balken, dazwischen wird linear gefuellt.
    progressFor(tiers, reached) {
      if (reached >= tiers.length - 1) return 100;
      const from = reached >= 0 ? tiers[reached].amount : 0;
      const to = tiers[reached + 1].amount;
      const segment = Math.min(Math.max((this.total - from) / (to - from), 0), 1);
      return ((reached + 1 + segment) / tiers.length) * 100;
    }

    tierLabel(tier) {
      return tier.type === 'shipping' ? 'Versandfrei' : `${tier.percent} % Rabatt`;
    }

    renderMessage(tiers, reached, qualified, discount) {
      if (!this.messageEl) return;
      this.messageEl.textContent = '';

      if (qualified) {
        const icon = document.createElement('span');
        icon.className = 'free-shipping-bar__success-icon';
        icon.setAttribute('aria-hidden', 'true');
        icon.innerHTML =
          '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" focusable="false"><circle cx="12" cy="12" r="11" fill="currentColor"/><path d="M7 12.5l3.2 3.2L17 9" stroke="#fff" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
        const text = document.createElement('span');
        text.className = 'free-shipping-bar__success-text';
        text.textContent = discount > 0 ? this.textAll.replace('[percent]', discount) : this.textSuccess;
        this.messageEl.append(icon, text);
        return;
      }

      const next = tiers[reached + 1];
      const goal = next.type === 'shipping' ? 'zur versandkostenfreien Lieferung' : this.tierLabel(next);
      const [before, after] = this.textNext.replace('[goal]', goal).split('[amount]');
      const strong = document.createElement('strong');
      strong.className = 'free-shipping-bar__amount';
      strong.textContent = this.formatMoney(next.amount - this.total);
      this.messageEl.append(document.createTextNode(before || ''), strong, document.createTextNode(after || ''));
    }

    renderSteps(tiers, reached) {
      if (this.markersEl) this.markersEl.textContent = '';
      if (this.labelsEl) this.labelsEl.textContent = '';

      tiers.forEach((tier, i) => {
        const position = `${((i + 1) / tiers.length) * 100}%`;
        const state = i <= reached ? ' is-reached' : '';
        const last = i === tiers.length - 1 ? ' is-last' : '';

        if (this.markersEl) {
          const marker = document.createElement('span');
          marker.className = `free-shipping-bar__marker${state}`;
          marker.style.left = position;
          this.markersEl.append(marker);
        }

        if (this.labelsEl) {
          const label = document.createElement('span');
          label.className = `free-shipping-bar__step${state}${last}`;
          label.style.left = position;
          const amount = document.createElement('strong');
          amount.className = 'free-shipping-bar__step-amount';
          amount.textContent = this.formatMoney(tier.amount, true);
          const name = document.createElement('span');
          name.className = 'free-shipping-bar__step-name';
          name.textContent = this.tierLabel(tier);
          label.append(amount, name);
          this.labelsEl.append(label);
        }
      });
    }

    renderDiscount(country, discount) {
      if (!this.discountEl) return;
      this.discountEl.textContent = '';
      this.discountEl.hidden = discount <= 0;
      if (discount <= 0) return;

      const saving = Math.round((this.total * discount) / 100);
      const row = (label, value, modifier) => {
        const line = document.createElement('div');
        line.className = `free-shipping-bar__discount-row free-shipping-bar__discount-row--${modifier}`;
        const left = document.createElement('span');
        left.textContent = label;
        const right = document.createElement('span');
        right.textContent = value;
        line.append(left, right);
        return line;
      };

      this.discountEl.append(
        row(`Rabatt bei Lieferung nach ${country.name} (${discount} %)`, `−${this.formatMoney(saving)}`, 'saving'),
        row('Warenwert nach Rabatt', this.formatMoney(this.total - saving), 'total')
      );
    }

    renderCountry(country) {
      if (!this.countryEl) return;
      this.countryEl.textContent = '';
      this.countryEl.hidden = this.countries.length < 2;
      if (this.countries.length < 2) return;

      const head = document.createElement('div');
      head.className = 'free-shipping-bar__country-head';
      const current = document.createElement('span');
      current.textContent = `Lieferland: ${country.name}`;

      const select = document.createElement('select');
      select.className = 'free-shipping-bar__country-select';
      select.setAttribute('aria-label', 'Lieferland');
      select.hidden = true;
      this.countries.forEach((c) => {
        const option = document.createElement('option');
        option.value = c.code;
        option.textContent = c.name;
        option.selected = c.code === country.code;
        select.append(option);
      });
      select.addEventListener('change', () => {
        storeCountry(select.value);
        document.dispatchEvent(new CustomEvent(FSB_COUNTRY_EVENT));
      });

      const toggle = document.createElement('button');
      toggle.type = 'button';
      toggle.className = 'free-shipping-bar__country-toggle';
      toggle.textContent = 'ändern';
      toggle.addEventListener('click', () => {
        toggle.hidden = true;
        current.hidden = true;
        select.hidden = false;
        select.focus();
      });

      head.append(current, select, toggle);
      this.countryEl.append(head);

      this.countries
        .filter((c) => c.code !== country.code)
        .forEach((c) => {
          const hint = document.createElement('p');
          hint.className = 'free-shipping-bar__country-hint';
          const parts = c.tiers.map((tier) =>
            tier.type === 'shipping'
              ? `versandkostenfrei ab ${this.formatMoney(tier.amount, true)}`
              : `${tier.percent} % Rabatt ab ${this.formatMoney(tier.amount, true)}`
          );
          hint.textContent = `Lieferung nach ${c.name}: ${parts.join(', ')}`;
          this.countryEl.append(hint);
        });
    }

    formatMoney(cents, stripZeros = false) {
      const value = cents || 0;
      const fmt = (decimals, thousands, decimalSep) => {
        const amount = (value / 100).toFixed(decimals);
        let [int, dec] = amount.split('.');
        int = int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
        if (stripZeros && dec && /^0+$/.test(dec)) return int;
        return dec ? `${int}${decimalSep}${dec}` : int;
      };
      return this.moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, token) => {
        switch (token) {
          case 'amount_no_decimals': return fmt(0, ',', '.');
          case 'amount_with_comma_separator': return fmt(2, '.', ',');
          case 'amount_no_decimals_with_comma_separator': return fmt(0, '.', ',');
          case 'amount_with_apostrophe_separator': return fmt(2, "'", '.');
          default: return fmt(2, ',', '.');
        }
      });
    }
  }

  customElements.define('free-shipping-bar', FreeShippingBar);
}
